import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import EmergencySection from "@/components/EmergencySection";
import ReportModal from "@/components/ReportModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Car, MapPin, Navigation, Clock, DollarSign, CheckCircle, Play,
  Power, PowerOff, User, Phone, Mail, Star, Bell, MessageSquare, Send, History, AlertTriangle, Locate, Loader2,
  Upload, FileText, Eye, XCircle, Clock as ClockIcon
} from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      if (options.body instanceof FormData) {
        xhr.send(options.body);
      } else {
        xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
      }
    } else {
      xhr.send();
    }
  });
};

const statusColors = {
  requested: "bg-amber-100 text-amber-700", assigned: "bg-blue-100 text-blue-700",
  accepted: "bg-indigo-100 text-indigo-700", started: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700",
};
const statusSteps = ["requested", "assigned", "accepted", "started", "completed"];

export default function DriverDashboard() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  const [rides, setRides] = useState([]);
  const [driverInfo, setDriverInfo] = useState(null);
  const [isOnline, setIsOnline] = useState(false);

  // Messages
  const [messageDialog, setMessageDialog] = useState({ open: false, ride: null });
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  // Notifications
  const [notifications, setNotifications] = useState([]);
  // Ratings
  const [driverRatings, setDriverRatings] = useState({ ratings: [], average: 5, count: 0 });
  // Reports
  const [reportModal, setReportModal] = useState({ open: false, rideId: null });
  // Rate Passenger
  const [ratePassengerDialog, setRatePassengerDialog] = useState({ open: false, ride: null });
  const [passengerRating, setPassengerRating] = useState(5);
  const [passengerReview, setPassengerReview] = useState("");
  // Location sharing
  const [sharingLocation, setSharingLocation] = useState(false);
  // Documents
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
  const fetchOpts = token ? { headers } : { headers, credentials: "include" };

  const fetchRides = useCallback(async () => { try { const r = await safeFetch(`${API}/driver/rides`, fetchOpts); if (r.ok) setRides(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchDriverInfo = useCallback(async () => { try { const r = await safeFetch(`${API}/driver/profile`, fetchOpts); if (r.ok) { const d = await r.json(); setDriverInfo(d); setIsOnline(d.status === "online"); } } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchNotifications = useCallback(async () => { try { const r = await safeFetch(`${API}/notifications`, fetchOpts); if (r.ok) setNotifications(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchRatings = useCallback(async () => {
    if (!user?.user_id) return;
    try { const r = await safeFetch(`${API}/ratings/driver/${user.user_id}`); if (r.ok) setDriverRatings(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, [user?.user_id]);

  useEffect(() => { fetchRides(); fetchDriverInfo(); }, []);
  useEffect(() => { if (activeTab === "messages") fetchNotifications(); if (activeTab === "profile") fetchRatings(); }, [activeTab]);

  const toggleStatus = async () => {
    try {
      const r = await safeFetch(`${API}/driver/status`, { method: "PUT", ...fetchOpts, body: JSON.stringify({ status: isOnline ? "offline" : "online" }) });
      if (r.ok) { setIsOnline(!isOnline); toast.success(isOnline ? "You're offline" : "You're online"); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleAccept = async (rideId) => {
    try { const r = await safeFetch(`${API}/driver/rides/${rideId}/accept`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Ride accepted"); fetchRides(); } else toast.error((await r.json()).detail); } catch (e) { console.error("Operation failed:", e); }
  };
  const handleStart = async (rideId) => {
    try { const r = await safeFetch(`${API}/driver/rides/${rideId}/start`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Ride started"); fetchRides(); } else toast.error((await r.json()).detail); } catch (e) { console.error("Operation failed:", e); }
  };
  const handleComplete = async (rideId) => {
    try { const r = await safeFetch(`${API}/driver/rides/${rideId}/complete`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Ride completed"); fetchRides(); fetchDriverInfo(); } else toast.error((await r.json()).detail); } catch (e) { console.error("Operation failed:", e); }
  };

  // Share location for real-time tracking
  const shareLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    setSharingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const r = await safeFetch(`${API}/driver/location`, {
            method: "PUT",
            ...fetchOpts,
            body: JSON.stringify({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            })
          });
          if (r.ok) {
            toast.success("Location updated for customers");
          } else {
            toast.error("Failed to update location");
          }
        } catch (e) {
          toast.error("Network error");
        }
        setSharingLocation(false);
      },
      (error) => {
        setSharingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error("Location permission denied. Please enable in browser settings.");
        } else {
          toast.error("Could not get your location");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleDocUpload = async (file, fieldKey) => {
    if (!file) return;
    setUploadingDoc(fieldKey);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const upRes = await safeFetch(`${API}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!upRes.ok) { toast.error("Upload failed"); setUploadingDoc(null); return; }
      const { url } = await upRes.json();
      const saveRes = await safeFetch(`${API}/drivers/documents`, {
        method: "PUT",
        ...fetchOpts,
        body: JSON.stringify({ [fieldKey]: url }),
      });
      if (saveRes.ok) { toast.success("Document uploaded"); fetchDriverInfo(); }
      else toast.error("Save failed");
    } catch (e) { console.error(e); toast.error("Upload error"); }
    setUploadingDoc(null);
  };

  const handleRatePassenger = async () => {
    if (!ratePassengerDialog.ride) return;
    try {
      const r = await safeFetch(`${API}/ratings/passenger`, {
        method: "POST", ...fetchOpts,
        body: JSON.stringify({ ride_id: ratePassengerDialog.ride.ride_id, rating: passengerRating, review: passengerReview })
      });
      if (r.ok) {
        toast.success("Rating submitted");
        setRatePassengerDialog({ open: false, ride: null });
        setPassengerRating(5);
        setPassengerReview("");
      } else {
        toast.error((await r.json()).detail);
      }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const openMessages = async (ride) => {
    setMessageDialog({ open: true, ride });
    try { const r = await safeFetch(`${API}/messages/${ride.ride_id}`, fetchOpts); if (r.ok) setMessages(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  };

  const handleSendMessage = async () => {
    if (!messageDialog.ride || !newMessage.trim()) return;
    try {
      const r = await safeFetch(`${API}/messages`, { method: "POST", ...fetchOpts, body: JSON.stringify({ ride_id: messageDialog.ride.ride_id, content: newMessage }) });
      if (r.ok) { setNewMessage(""); const mr = await safeFetch(`${API}/messages/${messageDialog.ride.ride_id}`, fetchOpts); if (mr.ok) setMessages(await mr.json()); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const activeRides = rides.filter(r => !["completed", "cancelled"].includes(r.ride_status));
  const pastRides = rides.filter(r => ["completed", "cancelled"].includes(r.ride_status));
  const totalEarnings = pastRides.filter(r => r.ride_status === "completed").reduce((s, r) => s + (r.fare_amount || 0), 0);

  return (
    <DashboardLayout activeTab={activeTab} onTabChange={setActiveTab}>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Hi, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-slate-500 mt-1">Manage your rides and earnings</p>
        </div>
        <Button data-testid="toggle-status-btn" onClick={toggleStatus} className={`rounded-full ${isOnline ? "bg-emerald-500 hover:bg-emerald-600" : "bg-slate-400 hover:bg-slate-500"} text-white`}>
          {isOnline ? <><Power className="w-4 h-4 mr-2" /> Online</> : <><PowerOff className="w-4 h-4 mr-2" /> Offline</>}
        </Button>
      </div>

      {/* Dashboard */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Status" value={isOnline ? "Online" : "Offline"} color={isOnline ? "text-emerald-600" : "text-slate-400"} />
            <StatCard label="Active Rides" value={activeRides.length} color="text-blue-600" />
            <StatCard label="Total Earnings" value={`\u20B9${totalEarnings}`} color="text-forest" />
            <StatCard label="Rating" value={driverInfo?.rating || 5.0} color="text-amber-600" />
          </div>
          {activeRides.length > 0 && activeRides.map(ride => (
            <Card key={ride.ride_id} className="rounded-2xl border-0 shadow-md bg-gradient-to-br from-forest to-forest-dark text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3"><Badge className="bg-white/20 text-white rounded-full">{ride.ride_status}</Badge><span className="font-heading font-bold text-xl">{"\u20B9"}{ride.fare_amount}</span></div>
                <RideStatusBar status={ride.ride_status} light />
                <div className="space-y-2 mt-3">
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-400" /><span className="text-white/90 text-sm">{ride.pickup_location}</span></div>
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-400" /><span className="text-white/90 text-sm">{ride.drop_location}</span></div>
                </div>
                <div className="flex gap-2 mt-4">
                  {ride.ride_status === "assigned" && <Button data-testid={`accept-ride-${ride.ride_id}`} onClick={() => handleAccept(ride.ride_id)} className="bg-white text-forest rounded-full flex-1">Accept</Button>}
                  {ride.ride_status === "accepted" && <Button data-testid={`start-ride-${ride.ride_id}`} onClick={() => handleStart(ride.ride_id)} className="bg-white text-forest rounded-full flex-1"><Play className="w-4 h-4 mr-1" /> Start</Button>}
                  {ride.ride_status === "started" && <Button data-testid={`complete-ride-${ride.ride_id}`} onClick={() => handleComplete(ride.ride_id)} className="bg-white text-forest rounded-full flex-1"><CheckCircle className="w-4 h-4 mr-1" /> Complete</Button>}
                  {ride.customer_id && <Button variant="outline" onClick={() => openMessages(ride)} className="rounded-full border-white/30 text-white hover:bg-white/10"><MessageSquare className="w-4 h-4" /></Button>}
                  {["accepted", "started"].includes(ride.ride_status) && (
                    <Button 
                      data-testid={`share-location-${ride.ride_id}`}
                      variant="outline" 
                      onClick={shareLocation} 
                      disabled={sharingLocation}
                      className="rounded-full border-white/30 text-white hover:bg-white/10"
                    >
                      {sharingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Rides Tab */}
      {activeTab === "rides" && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Assigned Rides</h2>
          {activeRides.length === 0 ? (
            <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><Car className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No assigned rides</p></CardContent></Card>
          ) : activeRides.map(ride => (
            <RideCard key={ride.ride_id} ride={ride} onAccept={handleAccept} onStart={handleStart} onComplete={handleComplete} onMessage={openMessages} onReport={rideId => setReportModal({ open: true, rideId })} />
          ))}
        </div>
      )}

      {/* Earnings Tab */}
      {activeTab === "earnings" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Earnings</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-2xl border-0 shadow-md bg-gradient-to-br from-forest to-forest-dark text-white"><CardContent className="p-6"><p className="text-white/70 text-sm">Total Earnings</p><p className="font-heading font-bold text-4xl mt-2">{"\u20B9"}{totalEarnings}</p></CardContent></Card>
            <StatCard label="Completed Rides" value={pastRides.filter(r => r.ride_status === "completed").length} color="text-emerald-600" />
            <StatCard label="Avg Rating" value={driverInfo?.rating || 5.0} color="text-amber-600" />
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Ride History</h2>
          {pastRides.length === 0 ? <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><History className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No past rides</p></CardContent></Card> :
            pastRides.map(ride => <RideCard key={ride.ride_id} ride={ride} onRatePassenger={r => { setRatePassengerDialog({ open: true, ride: r }); setPassengerRating(5); setPassengerReview(""); }} onReport={rideId => setReportModal({ open: true, rideId })} />)}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === "documents" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900">My Documents</h2>
              <p className="text-xs text-slate-500 mt-1">Upload or replace your verification documents</p>
            </div>
            <Badge className={`rounded-full text-xs ${driverInfo?.approval_status === "approved" ? "bg-emerald-100 text-emerald-700" : driverInfo?.approval_status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
              {driverInfo?.approval_status || "pending"}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: "profile_photo", label: "Profile Photo" },
              { key: "aadhaar_url", label: "Aadhaar Card" },
              { key: "driving_license_url", label: "Driving License" },
              { key: "vehicle_rc_url", label: "Vehicle RC" },
              { key: "insurance_url", label: "Insurance" },
            ].map(({ key, label }) => {
              const docUrl = driverInfo?.documents?.[key];
              const fullUrl = docUrl ? (docUrl.startsWith("http") ? docUrl : `${API.replace("/api", "")}${docUrl.startsWith("/") ? docUrl : "/" + docUrl}`) : null;
              const isPdf = docUrl && docUrl.toLowerCase().endsWith(".pdf");
              const uploading = uploadingDoc === key;
              return (
                <Card key={key} data-testid={`driver-doc-${key}`} className="rounded-2xl border-0 shadow-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-slate-800">{label}</span>
                      {docUrl ? (
                        <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-xs">Uploaded</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 rounded-full text-xs">Not uploaded</Badge>
                      )}
                    </div>
                    <div className="w-full h-32 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center border border-slate-200">
                      {docUrl ? (
                        isPdf ? (
                          <div className="flex flex-col items-center text-slate-500"><FileText className="w-8 h-8" /><span className="text-xs mt-1">PDF</span></div>
                        ) : (
                          <img src={fullUrl} alt={label} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                        )
                      ) : (
                        <div className="flex flex-col items-center text-slate-300"><AlertTriangle className="w-6 h-6" /><span className="text-xs mt-1">No file</span></div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <input
                          data-testid={`upload-input-${key}`}
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => handleDocUpload(e.target.files?.[0], key)}
                          disabled={uploading}
                        />
                        <Button asChild size="sm" className="w-full rounded-xl bg-forest text-white text-xs cursor-pointer" disabled={uploading}>
                          <span>
                            {uploading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading</> : <><Upload className="w-3 h-3 mr-1" /> {docUrl ? "Replace" : "Upload"}</>}
                          </span>
                        </Button>
                      </label>
                      {docUrl && (
                        <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="rounded-xl text-xs"><Eye className="w-3 h-3" /></Button>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {driverInfo?.approval_status === "pending" && (
            <Card className="rounded-2xl border-0 shadow-sm bg-amber-50">
              <CardContent className="p-4 flex items-start gap-3">
                <ClockIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Awaiting Admin Approval</p>
                  <p className="text-xs text-amber-700 mt-1">Your documents are being reviewed. Make sure all 5 are uploaded for faster approval.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === "messages" && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Messages</h2>
          {activeRides.length === 0 ? <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No active ride chats</p></CardContent></Card> :
            activeRides.map(ride => (
              <Card key={ride.ride_id} className="rounded-2xl border-0 shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => openMessages(ride)}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">
                      {ride.customer_name}
                      {ride.passenger_count > 1 && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{ride.passenger_count} pax</span>}
                    </p>
                    <p className="text-xs text-slate-400">{ride.pickup_location} → {ride.drop_location}</p>
                    {ride.passenger_names?.length > 0 && (
                      <p className="text-[10px] text-slate-400 mt-0.5">Passengers: {ride.passenger_names.filter(Boolean).join(", ")}</p>
                    )}
                  </div>
                  <Badge className={`rounded-full text-xs ${statusColors[ride.ride_status]}`}>{ride.ride_status}</Badge>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === "profile" && (
        <div className="max-w-lg space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">My Profile</h2>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-forest-50 flex items-center justify-center"><span className="font-heading font-bold text-2xl text-forest">{user?.name?.[0]?.toUpperCase()}</span></div>
                <div><p className="font-heading font-semibold text-lg">{user?.name}</p><p className="text-sm text-slate-500">Driver</p></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-600"><Phone className="w-5 h-5 text-slate-400" /><span>{user?.phone || "-"}</span></div>
                <div className="flex items-center gap-3 text-slate-600"><Car className="w-5 h-5 text-slate-400" /><span>{driverInfo?.vehicle_type} - {driverInfo?.vehicle_number}</span></div>
                <div className="flex items-center gap-3 text-slate-600"><DollarSign className="w-5 h-5 text-slate-400" /><span>Total Earnings: {"\u20B9"}{driverInfo?.total_earnings || 0}</span></div>
                <div className="flex items-center gap-3 text-slate-600"><Star className="w-5 h-5 text-amber-400" /><span>Rating: {driverInfo?.rating || 5.0} ({driverRatings.count} reviews)</span></div>
              </div>
            </CardContent>
          </Card>
          {/* Recent Ratings */}
          {driverRatings.ratings.length > 0 && (
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Star className="w-5 h-5 text-amber-400" /> Recent Reviews</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {driverRatings.ratings.slice(0, 10).map(r => (
                  <div key={r.rating_id} className="p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex">{[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
                      <span className="text-xs text-slate-500">{r.user_name}</span>
                    </div>
                    {r.review && <p className="text-sm text-slate-700">{r.review}</p>}
                    <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== EMERGENCY ===== */}
      {activeTab === "emergency" && (
        <div className="max-w-lg">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">Emergency & Safety</h2>
          <EmergencySection token={token} rideId={activeRides[0]?.ride_id} />
        </div>
      )}

      {/* Message Dialog */}
      <Dialog open={messageDialog.open} onOpenChange={o => setMessageDialog({ ...messageDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Chat - {messageDialog.ride?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {messages.length === 0 ? <p className="text-center text-slate-400 py-4">No messages</p> :
              messages.map(m => (
                <div key={m.message_id} className={`p-3 rounded-xl ${m.sender_id === user?.user_id ? "bg-forest-50 ml-8" : "bg-slate-50 mr-8"}`}>
                  <p className="text-xs font-medium text-slate-600 mb-1">{m.sender_name}</p>
                  <p className="text-sm text-slate-800">{m.content}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                </div>
              ))}
          </div>
          <div className="flex gap-2">
            <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a message..." className="rounded-xl" onKeyDown={e => e.key === "Enter" && handleSendMessage()} />
            <Button onClick={handleSendMessage} className="bg-forest text-white rounded-xl"><Send className="w-4 h-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rate Passenger Dialog */}
      <Dialog open={ratePassengerDialog.open} onOpenChange={o => setRatePassengerDialog({ ...ratePassengerDialog, open: o })}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Rate Passenger</DialogTitle>
          </DialogHeader>
          {ratePassengerDialog.ride && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">How was your experience with <span className="font-medium">{ratePassengerDialog.ride.customer_name}</span>?</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map(v => (
                  <button key={v} onClick={() => setPassengerRating(v)} className={`p-2 rounded-full transition-all ${passengerRating >= v ? "text-amber-500 scale-110" : "text-slate-300"}`}>
                    <Star className="w-8 h-8 fill-current" />
                  </button>
                ))}
              </div>
              <Textarea
                value={passengerReview}
                onChange={e => setPassengerReview(e.target.value)}
                placeholder="Leave a review (optional)..."
                className="rounded-xl"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatePassengerDialog({ open: false, ride: null })} className="rounded-xl">Cancel</Button>
            <Button onClick={handleRatePassenger} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">Submit Rating</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModal.open}
        onClose={() => setReportModal({ open: false, rideId: null })}
        rideId={reportModal.rideId}
        token={token}
      />
    </DashboardLayout>
  );
}

function StatCard({ label, value, color = "text-slate-900" }) {
  return (
    <Card className="rounded-2xl border-0 shadow-sm">
      <CardContent className="p-5"><p className="text-sm text-slate-500">{label}</p><p className={`font-heading font-bold text-3xl mt-1 ${color}`}>{value}</p></CardContent>
    </Card>
  );
}

function RideStatusBar({ status, light }) {
  const idx = statusSteps.indexOf(status);
  return (
    <div className="flex items-center gap-1 my-2">
      {statusSteps.map((s, i) => (
        <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= idx ? (light ? "bg-white/80" : "bg-forest") : (light ? "bg-white/20" : "bg-slate-200")}`} />
      ))}
    </div>
  );
}

function RideCard({ ride, onAccept, onStart, onComplete, onMessage, onReport, onRatePassenger }) {
  return (
    <Card data-testid={`ride-card-${ride.ride_id}`} className="rounded-2xl border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge className={`rounded-full text-xs px-3 py-1 ${statusColors[ride.ride_status]}`}>{ride.ride_status}</Badge>
            <span className="text-xs text-slate-500">{ride.service_type?.replace(/_/g, " ")}</span>
          </div>
          <span className="font-heading font-bold text-lg text-slate-900">{"\u20B9"}{ride.fare_amount}</span>
        </div>
        <RideStatusBar status={ride.ride_status} />
        <div className="space-y-2 text-sm mt-2">
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-slate-700">{ride.pickup_location}</span></div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-slate-700">{ride.drop_location}</span></div>
        </div>
        <p className="text-xs text-slate-500 mt-2">Customer: {ride.customer_name}{ride.passenger_count > 1 && ` (${ride.passenger_count} passengers)`}</p>
        {ride.passenger_names?.filter(Boolean).length > 0 && (
          <p className="text-[10px] text-slate-400">Passengers: {ride.passenger_names.filter(Boolean).join(", ")}</p>
        )}
        {ride.created_at && <p className="text-xs text-slate-400 mt-1">{new Date(ride.created_at).toLocaleString()}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          {ride.ride_status === "assigned" && onAccept && <Button data-testid={`accept-ride-${ride.ride_id}`} size="sm" onClick={() => onAccept(ride.ride_id)} className="rounded-full bg-blue-600 text-white text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Accept</Button>}
          {ride.ride_status === "accepted" && onStart && <Button data-testid={`start-ride-${ride.ride_id}`} size="sm" onClick={() => onStart(ride.ride_id)} className="rounded-full bg-emerald-600 text-white text-xs">Start Ride</Button>}
          {ride.ride_status === "started" && onComplete && <Button data-testid={`complete-ride-${ride.ride_id}`} size="sm" onClick={() => onComplete(ride.ride_id)} className="rounded-full bg-green-600 text-white text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Complete</Button>}
          {onMessage && ride.customer_id && !["cancelled", "completed"].includes(ride.ride_status) && <Button variant="outline" size="sm" onClick={() => onMessage(ride)} className="rounded-full text-xs"><MessageSquare className="w-3 h-3 mr-1" /> Chat</Button>}
          {onReport && ride.customer_id && !["cancelled"].includes(ride.ride_status) && (
            <Button data-testid={`report-ride-${ride.ride_id}`} variant="outline" size="sm" onClick={() => onReport(ride.ride_id)} className="rounded-full text-xs text-red-500 border-red-200 hover:bg-red-50">
              <AlertTriangle className="w-3 h-3 mr-1" /> Report
            </Button>
          )}
          {onRatePassenger && ride.ride_status === "completed" && (
            <Button data-testid={`rate-passenger-${ride.ride_id}`} variant="outline" size="sm" onClick={() => onRatePassenger(ride)} className="rounded-full text-xs text-amber-600 border-amber-200">
              <Star className="w-3 h-3 mr-1" /> Rate
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
